
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Clock, BookOpen, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { RichTextEditor } from './RichTextEditor';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

const formSchema = z.object({
  title: z.string().min(1, { message: 'Title is required' }),
  description: z.string().optional(),
  difficulty: z.string().optional(),
  content: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

export const ModuleEditor = () => {
  const { moduleId } = useParams<{ moduleId: string }>();
  const isNewModule = moduleId === 'new';
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(!isNewModule);
  const [isSaving, setIsSaving] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: '',
      description: '',
      difficulty: 'Beginner',
      content: '',
    },
  });

  useEffect(() => {
    if (!isNewModule) {
      fetchModule();
    }
  }, [moduleId]);

  const fetchModule = async () => {
    try {
      const { data, error } = await supabase
        .from('learning_modules')
        .select('*')
        .eq('id', moduleId)
        .single();

      if (error) throw error;
      
      if (data) {
        form.reset({
          title: data.title || '',
          description: data.description || '',
          difficulty: data.difficulty || 'Beginner',
          content: data.content || '',
        });
      }
    } catch (error) {
      console.error('Error fetching module:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load module details. Please try again.',
      });
      navigate('/admin/modules');
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (values: FormValues) => {
    setIsSaving(true);
    try {
      if (isNewModule) {
        // Create new module
        const { data, error } = await supabase
          .from('learning_modules')
          .insert([{
            title: values.title,
            description: values.description,
            difficulty: values.difficulty,
            content: values.content
          }])
          .select()
          .single();
        
        if (error) throw error;
        
        toast({
          title: 'Module created',
          description: 'The module has been successfully created.',
        });
        
        navigate(`/admin/modules/${data.id}`);
      } else {
        // Update existing module
        const { error } = await supabase
          .from('learning_modules')
          .update({
            title: values.title,
            description: values.description,
            difficulty: values.difficulty,
            content: values.content
          })
          .eq('id', moduleId);
        
        if (error) throw error;
        
        toast({
          title: 'Module updated',
          description: 'The module has been successfully updated.',
        });
      }
    } catch (error) {
      console.error('Error saving module:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: `Failed to ${isNewModule ? 'create' : 'update'} module. Please try again.`,
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Button variant="ghost" onClick={() => navigate('/admin/modules')} className="pl-0">
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Modules
      </Button>
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{isNewModule ? 'Create New Module' : 'Edit Module'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter module title" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Input placeholder="Brief description of this module" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="difficulty"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      <div className="flex items-center">
                        <BookOpen className="h-4 w-4 mr-1" />
                        Difficulty
                      </div>
                    </FormLabel>
                    <Select 
                      onValueChange={field.onChange} 
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select difficulty" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Beginner">Beginner</SelectItem>
                        <SelectItem value="Intermediate">Intermediate</SelectItem>
                        <SelectItem value="Advanced">Advanced</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Module Content</CardTitle>
            </CardHeader>
            <CardContent>
              <FormField
                control={form.control}
                name="content"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <RichTextEditor
                        initialValue={field.value || ''}
                        onChange={field.onChange}
                        minHeight="400px"
                      />
                    </FormControl>
                    <FormDescription>
                      Use Markdown to format your content. The toolbar above provides common formatting options.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>
          
          <div className="flex justify-end">
            <Button type="submit" disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Module
                </>
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};
